import { useAuth } from '@pooflabs/web';
import { AlertTriangle, Cpu, Plus, Server, Star, Wallet, Zap } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import TopNav from '@/components/TopNav';
import { NodeCard } from '@/components/NodeCard';
import { NodeRegistrationForm } from '@/components/NodeRegistrationForm';
import { EmptyState, PageLayout } from '@/components/poof-ui';
import { NumberTicker } from '@/components/effects';
import { useRealtimeData } from '@/hooks/use-realtime-data';
import {
  aggregateGpuNodes,
  countGpuNodes,
  subscribeManyGpuNodes,
  type GpuNodesResponse,
} from '@/lib/collections/gpuNodes';
import { subscribeTokenConfig, type TokenConfigResponse } from '@/lib/collections/tokenConfig';
import { reputationPercent } from '@/utils/gpuNetwork';

/**
 * GPU Network — provider-earning dashboard. Users self-report a GPU node,
 * it passively accrues t-hive by tier/hour, and they claim it to their
 * wallet via the backend claim route.
 */
export const GPUNetworkPage: React.FC = () => {
  const { user, login } = useAuth();

  const { data: myNodes } = useRealtimeData<GpuNodesResponse[]>(
    subscribeManyGpuNodes,
    !!user?.address,
    `owner='${user?.address ?? ''}' limit 20`
  );

  const { data: tokenConfig } = useRealtimeData<TokenConfigResponse | null>(subscribeTokenConfig, true, 'main');

  const [networkNodes, setNetworkNodes] = useState(0);
  const [networkRate, setNetworkRate] = useState(0);
  const [avgReputationBps, setAvgReputationBps] = useState(0);
  const [showForm, setShowForm] = useState(false);

  useEffect(() => {
    countGpuNodes().then((res) => setNetworkNodes(res?.value ?? 0));
    aggregateGpuNodes('sum', { field: 'ratePerHour' }).then((res) => setNetworkRate(res?.value ?? 0));
    aggregateGpuNodes('avg', { field: 'reputationScore', filter: "status='active'" }).then((res) =>
      setAvgReputationBps(res?.value ?? 0)
    );
  }, []);

  const nodes = myNodes ?? [];
  const tokenReady = !!tokenConfig?.created && !!tokenConfig?.mint;

  return (
    <PageLayout background="" fullBleed footer={false}>
      <TopNav />

      <div className="container pb-24 lg:pb-10">
        {/* ── Header ── */}
        <header className="pt-10 pb-2 animate-fade-in-up">
          <div className="inline-flex items-center gap-1.5 rounded-full bg-secondary/70 px-3 py-1 text-[11px] font-bold uppercase tracking-wide text-muted-foreground mb-3">
            <Server className="w-3 h-3" />
            Compute network
          </div>
          <h1 className="text-headline font-extrabold tracking-tight">GPU Network</h1>
          <p className="text-muted-foreground mt-1.5 max-w-xl">
            Register a self-hosted or AWS-hosted GPU, earn t-hive every 30 seconds while it's active, and pause/resume
            whenever your server goes offline. Higher tiers earn more per tick.
          </p>
        </header>

        {/* ── Network-wide stat strip (visible to everyone) ── */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-8 animate-fade-in-up delay-1">
          <div className="rounded-2xl bg-card border border-border p-4 shadow-sm">
            <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-1">
              <Cpu className="w-3 h-3" /> Nodes online
            </p>
            <p className="text-xl font-extrabold mt-1.5 tabular-nums">
              <NumberTicker value={networkNodes} />
            </p>
          </div>
          <div className="rounded-2xl bg-card border border-border p-4 shadow-sm">
            <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-1">
              <Zap className="w-3 h-3" /> Network rate
            </p>
            <p className="text-xl font-extrabold mt-1.5 tabular-nums">
              <NumberTicker value={networkRate} /> <span className="text-xs font-bold text-primary">t-hive/hr</span>
            </p>
          </div>
          <div className="rounded-2xl bg-card border border-border p-4 shadow-sm">
            <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-1">
              <Star className="w-3 h-3" /> Avg reputation
            </p>
            <p className="text-xl font-extrabold mt-1.5 tabular-nums">
              <NumberTicker value={reputationPercent(avgReputationBps)} />
              <span className="text-xs font-bold text-primary">%</span>
            </p>
          </div>
          <div className="rounded-2xl bg-card border border-border p-4 shadow-sm hidden sm:block">
            <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide">Your nodes</p>
            <p className="text-xl font-extrabold mt-1.5 tabular-nums">
              <NumberTicker value={nodes.length} />
            </p>
          </div>
        </div>

        {!tokenReady && (
          <div className="mt-6 rounded-2xl border border-amber-300/60 bg-amber-50 px-4 py-3 flex items-center gap-2.5 text-sm text-amber-800 animate-fade-in-up delay-1">
            <AlertTriangle className="w-4 h-4 shrink-0" />
            t-hive token is still initializing. You can register nodes now, but claims will be enabled shortly.
          </div>
        )}

        {/* ── Logged-out state ── */}
        {!user && (
          <div className="mt-10 animate-fade-in-up delay-2">
            <EmptyState
              icon={<Wallet />}
              title="Connect your wallet"
              description="Sign in to register a GPU node and start earning t-hive passively."
              action={
                <button
                  onClick={() => login()}
                  className="rounded-full bg-[#00C805] text-white font-bold px-6 py-3 hover:bg-[#00b004] transition-colors"
                >
                  Connect wallet
                </button>
              }
            />
          </div>
        )}

        {/* ── Logged-in: your nodes ── */}
        {user && (
          <section className="mt-10 animate-fade-in-up delay-2">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-bold text-lg">Your nodes</h2>
              {nodes.length > 0 && (
                <button
                  onClick={() => setShowForm((v) => !v)}
                  className="text-sm font-semibold text-primary inline-flex items-center gap-1 transition-transform duration-300 hover:translate-x-0.5"
                >
                  <Plus className="w-3.5 h-3.5" /> {showForm ? 'Hide form' : 'Register another node'}
                </button>
              )}
            </div>

            {nodes.length === 0 && (
              <div className="mb-6">
                <EmptyState
                  icon={<Cpu />}
                  title="No GPU registered yet"
                  description="Register your first node below — specs are self-reported, no hardware check required."
                />
              </div>
            )}

            {(showForm || nodes.length === 0) && (
              <div className="mb-6">
                <NodeRegistrationForm
                  onRegistered={() => setShowForm(false)}
                  onCancel={nodes.length > 0 ? () => setShowForm(false) : undefined}
                />
              </div>
            )}

            {nodes.length > 0 && (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {nodes.map((node) => (
                  <NodeCard key={node.id} node={node} walletAddress={user.address} tokenReady={tokenReady} />
                ))}
              </div>
            )}
          </section>
        )}
      </div>
    </PageLayout>
  );
};

export default GPUNetworkPage;
